/**
 * Regole pure della chat Instagram/Messenger (niente React, niente Supabase):
 * formati degli allegati, reazioni per canale, ricerca nel thread, testi dei
 * messaggi speciali (eliminati, non supportati, pulsanti, storie) e referral.
 */

export type MetaChannel = 'instagram' | 'messenger';

// ── Allegati ────────────────────────────────────────────────────────────────
// Doc Instagram Messaging (Send API): foto png/jpeg fino a 8 MB; audio aac, m4a,
// wav, mp4; video mp4, ogg, avi, mov, webm; file solo PDF. Il bucket del CRM
// tiene fino a 16 MB, quindi video/audio/PDF oltre i 16 MB non partono.
// Messenger accetta in piu' le GIF (doc Messenger "Send a message": image jpg, png, gif).

const IMAGE_MIMES = ['image/png', 'image/jpeg'];
const AUDIO_MIMES = ['audio/aac', 'audio/x-aac', 'audio/mp4', 'audio/m4a', 'audio/x-m4a', 'audio/wav', 'audio/x-wav', 'audio/wave'];
const VIDEO_MIMES = ['video/mp4', 'video/ogg', 'video/x-msvideo', 'video/avi', 'video/quicktime', 'video/webm'];
const FILE_MIMES = ['application/pdf'];

/** Estensione → MIME, per i file che il browser consegna senza tipo (capita con .m4a). */
const EXTENSION_MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  aac: 'audio/aac',
  m4a: 'audio/mp4',
  wav: 'audio/wav',
  mp4: 'video/mp4',
  ogg: 'video/ogg',
  avi: 'video/x-msvideo',
  mov: 'video/quicktime',
  webm: 'video/webm',
  pdf: 'application/pdf',
};

export function allowedMimes(channel: MetaChannel): string[] {
  const base = [...IMAGE_MIMES, ...AUDIO_MIMES, ...VIDEO_MIMES, ...FILE_MIMES];
  return channel === 'messenger' ? [...base, 'image/gif'] : base;
}

/** Valore dell'attributo `accept` del selettore file (MIME + estensioni). */
export function acceptAttribute(channel: MetaChannel): string {
  const exts = ['.png', '.jpg', '.jpeg', '.aac', '.m4a', '.wav', '.mp4', '.ogg', '.avi', '.mov', '.webm', '.pdf'];
  if (channel === 'messenger') exts.push('.gif');
  return [...allowedMimes(channel), ...exts].join(',');
}

/** MIME effettivo: quello del browser, o dedotto dall'estensione se manca. */
export function resolveMime(file: { name: string; type: string }): string {
  if (file.type) return file.type.toLowerCase();
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return EXTENSION_MIME[ext] ?? '';
}

const MB = 1024 * 1024;

/**
 * Controllo unico per selettore file, trascinamento e incolla: `null` se il file
 * puo' partire, altrimenti il messaggio da mostrare.
 */
export function attachmentProblem(file: { name: string; type: string; size: number }, channel: MetaChannel): string | null {
  const mime = resolveMime(file);
  const label = channel === 'instagram' ? 'Instagram' : 'Messenger';
  if (!mime || !allowedMimes(channel).includes(mime)) {
    return channel === 'instagram'
      ? `Formato non accettato da ${label}: usa foto PNG o JPEG, video MP4, MOV, WEBM, OGG o AVI, audio AAC, M4A o WAV, oppure un PDF`
      : `Formato non accettato da ${label}: usa foto PNG, JPEG o GIF, video MP4, MOV, WEBM, OGG o AVI, audio AAC, M4A o WAV, oppure un PDF`;
  }
  if (mime.startsWith('image/') && file.size > 8 * MB) return 'Foto troppo grande: massimo 8 MB';
  if (file.size > 16 * MB) return 'File troppo grande: massimo 16 MB';
  return null;
}

// ── Etichette degli allegati ricevuti ──────────────────────────────────────
// Tipi del webhook Meta (message.attachments[].type). Quelli non previsti ("fallback",
// "template" e simili) non mostrano mai la parola tecnica: diventano "Allegato".

const ATTACHMENT_LABELS: Record<string, string> = {
  image: 'Immagine',
  video: 'Video',
  audio: 'Messaggio vocale',
  file: 'File',
  share: 'Contenuto condiviso',
  story_mention: 'Menzione in una storia',
  ig_reel: 'Reel',
  reel: 'Reel',
  ig_post: 'Post condiviso',
  fallback: 'Link condiviso',
  template: 'Messaggio da modello',
  location: 'Posizione',
  sticker: 'Adesivo',
  animated_image: 'Immagine animata',
};

/** Nome in italiano del tipo di allegato; "Allegato" per i tipi non previsti. */
export function metaAttachmentLabel(type: string | null | undefined): string {
  return (type && ATTACHMENT_LABELS[type]) || 'Allegato';
}

/** Scritta quando l'allegato non si puo' piu' aprire (link Meta scaduto o file rotto). */
export function metaAttachmentUnavailableLabel(type: string | null | undefined): string {
  return `${metaAttachmentLabel(type)} non più disponibile`;
}

// ── Reazioni ────────────────────────────────────────────────────────────────

export const HEART = '❤️';

/**
 * Instagram accetta solo la reazione "love" (doc Send API, "React to a message"):
 * nell'interfaccia si offre solo il cuore. Messenger accetta qualunque emoji.
 */
export function onlyHeartReaction(channel: MetaChannel): boolean {
  return channel === 'instagram';
}

// ── Ricerca nel thread ─────────────────────────────────────────────────────

interface SearchableMessage {
  id: string;
  content: string;
  deleted_at?: string | null;
}

/** Id dei messaggi che contengono il testo cercato, dal piu' recente al piu' vecchio. */
export function searchThread(messages: SearchableMessage[], query: string): string[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const out: string[] = [];
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (m.deleted_at) continue;
    if ((m.content || '').toLowerCase().includes(q)) out.push(m.id);
  }
  return out;
}

/** Spezza il testo nei tratti che corrispondono alla ricerca (per l'evidenziazione). */
export function highlightParts(text: string, query: string): Array<{ text: string; match: boolean }> {
  const q = query.trim();
  if (q.length < 2 || !text) return [{ text, match: false }];
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  const parts: Array<{ text: string; match: boolean }> = [];
  let from = 0;
  let at = lower.indexOf(needle, from);
  while (at !== -1) {
    if (at > from) parts.push({ text: text.slice(from, at), match: false });
    parts.push({ text: text.slice(at, at + needle.length), match: true });
    from = at + needle.length;
    at = lower.indexOf(needle, from);
  }
  if (from < text.length) parts.push({ text: text.slice(from), match: false });
  return parts;
}

// ── Messaggi speciali ──────────────────────────────────────────────────────

interface SpecialMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  meta_message_id?: string | null;
  deleted_at?: string | null;
  is_unsupported?: boolean | null;
  status?: string;
}

/** Testo al posto del contenuto: messaggio eliminato dal contatto o non supportato da Instagram. */
export function specialNotice(m: SpecialMessage, channel: MetaChannel): string | null {
  if (m.deleted_at) return 'Messaggio eliminato dal contatto';
  if (m.is_unsupported) {
    return channel === 'instagram'
      ? "Messaggio non supportato da Instagram, aprilo nell'app"
      : "Messaggio non supportato, aprilo nell'app";
  }
  return null;
}

/**
 * Rispondi e Reagisci servono il mid Meta: senza (bolla in invio, riga scritta
 * senza message_id, messaggio eliminato) non si offrono.
 */
export function canQuoteOrReact(m: SpecialMessage): boolean {
  return Boolean(m.meta_message_id) && !m.status && !m.deleted_at;
}

/** "Visualizzato" verso il contatto: solo se l'ultimo messaggio vero e' suo. */
export function lastMessageIsFromContact(messages: SpecialMessage[]): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (m.id.startsWith('temp-')) continue;
    return m.direction === 'inbound';
  }
  return false;
}

// ── Referral ───────────────────────────────────────────────────────────────

/** "Arrivato dal link: promo_autunno" (m.me / ig.me con ref, pubblicita', QR). */
export function referralLabel(c: { referral_ref?: string | null; referral_source?: string | null }): string | null {
  if (c.referral_ref) return `Arrivato dal link: ${c.referral_ref}`;
  if (!c.referral_source) return null;
  const source = c.referral_source.toUpperCase();
  if (source === 'ADS') return 'Arrivato da una pubblicità';
  if (source === 'SHORTLINK' || source === 'IGME') return 'Arrivato da un link diretto';
  return `Arrivato da: ${c.referral_source}`;
}
